/** Track uploads over time, above the log that explains them.
 *
 * The Logs screen is read a line at a time, and the question that sends
 * somebody to it about uploads is usually a shape: did a contributor just send
 * a backlog, is anything arriving, has the queue for the merge job been
 * growing since Tuesday. So the shape is drawn first — one column per hour or
 * per day, each upload filed under where it stands now — and the lines are
 * underneath for whoever wants to know why a column looks the way it does.
 *
 * Two plots share one time axis rather than one plot sharing two scales. The
 * refusals are not uploads: a contributor waiting out a `429` can be refused
 * forty times for one bundle, and on a common scale that would flatten the
 * columns that matter into the baseline. They get a strip of their own, with
 * its own scale, under the same hours.
 *
 * The columns are laid out by the browser, not computed into an SVG, so the
 * chart is as wide as the screen it is on and the marks stay the width they
 * were designed at. The whole slot is the hover target, not the mark — a
 * column one upload tall is two pixels high — and everything the hover says
 * is also in the table under the fold of the `details`, for a reader who
 * cannot hover.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { adminUploadTimeline } from '@/lib/api';
import { count, shortDate } from '@/lib/format';
import type { UploadTimeline, UploadTimelineBucket, UploadTimelineRange } from '@/lib/types';
import { Card, ErrorNote, Segmented, StatTile } from '@/components/ui';
import {
  SERIES_META,
  TIMELINE_SERIES,
  axisLabel,
  bucketTitle,
  bucketTotal,
  labelledIndexes,
  niceMax,
  ticksFor,
} from './uploadTimeline';

const RANGES: ReadonlyArray<{ value: UploadTimelineRange; label: string }> = [
  { value: '24h', label: '24 h' },
  { value: '7d', label: '7 d' },
  { value: '30d', label: '30 d' },
];

const PLOT_HEIGHT = 150;
const STRIP_HEIGHT = 34;
/** The gutter the y-axis numbers sit in; both plots leave it, so they align. */
const GUTTER = 34;
/** The surface the marks sit on, for the gap that separates stacked segments. */
const SURFACE = 'var(--color-surface)';

const MUTED = { color: 'var(--color-neutral-500)' } as const;
const AXIS_TEXT = {
  fontSize: 10.5,
  color: 'var(--color-neutral-500)',
  fontVariantNumeric: 'tabular-nums',
} as const;

function Swatch({ colour }: { colour: string }) {
  return (
    <span
      aria-hidden="true"
      style={{ display: 'inline-block', width: 9, height: 9, borderRadius: 2, background: colour }}
    />
  );
}

interface ColumnProps {
  bucket: UploadTimelineBucket;
  max: number;
  active: boolean;
}

/** One bucket's stack: merged at the baseline, then queued, held, rejected. */
function Column({ bucket, max, active }: ColumnProps) {
  const present = TIMELINE_SERIES.filter((series) => bucket[series] > 0);
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column-reverse',
        alignItems: 'stretch',
        width: 'min(22px, 72%)',
        height: '100%',
        opacity: active ? 1 : 0.92,
      }}
    >
      {present.map((series, index) => (
        <div
          key={series}
          style={{
            height: `${(bucket[series] / max) * 100}%`,
            // Never thinner than a mark a person can see: one upload in a
            // month of busy days is still an upload.
            minHeight: 3,
            background: SERIES_META[series].colour,
            // The gap is the surface showing through, not a stroke round the
            // mark; square at the baseline, rounded where the data ends.
            borderTop: index === present.length - 1 ? undefined : `2px solid ${SURFACE}`,
            borderRadius: index === present.length - 1 ? '4px 4px 0 0' : 0,
            boxSizing: 'border-box',
          }}
        />
      ))}
    </div>
  );
}

export function UploadTimelineCard() {
  const [range, setRange] = useState<UploadTimelineRange>('24h');
  const [data, setData] = useState<UploadTimeline | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [hover, setHover] = useState<number | null>(null);

  const request = useRef(0);
  const load = useCallback((next: UploadTimelineRange) => {
    const mine = ++request.current;
    setLoading(true);
    setError(null);
    adminUploadTimeline(next).then(
      (reply) => {
        if (request.current !== mine) return;
        setData(reply);
        setLoading(false);
      },
      (thrown: unknown) => {
        if (request.current !== mine) return;
        setError(thrown);
        setLoading(false);
      },
    );
  }, []);

  useEffect(() => {
    load(range);
    return () => {
      // A reply for a range nobody is looking at any more is dropped.
      request.current += 1;
    };
  }, [load, range]);

  const buckets = data?.buckets ?? [];
  const unit = data?.unit ?? 'hour';
  const max = niceMax(Math.max(0, ...buckets.map(bucketTotal)));
  const ticks = ticksFor(max);
  const refusedMax = niceMax(Math.max(0, ...buckets.map((bucket) => bucket.refused ?? 0)));
  const labelled = labelledIndexes(buckets.length, unit);
  const hovered = hover === null ? null : (buckets[hover] ?? null);
  const logStartsLate = data ? data.refused_from > data.from : false;
  const busiest = Math.max(1, ...(data?.top_circuits ?? []).map((circuit) => circuit.uploads));

  return (
    <Card label="Track uploads" as="section" style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <h2 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>Track uploads</h2>
          <p style={{ margin: '2px 0 0', fontSize: 12, ...MUTED }}>
            Submitted per {unit}, filed under where each stands now; queued is waiting for the merge
            job. Times are UTC.
          </p>
        </div>
        <div style={{ marginLeft: 'auto' }}>
          <Segmented<UploadTimelineRange>
            label="time range"
            value={range}
            options={RANGES.map((option) => ({ value: option.value, label: option.label }))}
            onChange={setRange}
          />
        </div>
      </div>

      {error ? (
        <ErrorNote error={error} title="the uploads could not be counted" onRetry={() => load(range)} compact />
      ) : null}

      {data ? (
        <>
          <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap', opacity: loading ? 0.6 : 1 }}>
            <StatTile size="md" value={count(data.totals.submitted)} label="submitted" sub={`last ${RANGES.find((r) => r.value === data.range)?.label}`} />
            <StatTile size="md" value={count(data.queued_now)} label="queued now" sub="all time, not yet merged" />
            <StatTile size="md" tone="muted" value={count(data.totals.circuits)} label="circuits" sub={`from ${count(data.totals.contributors)} ${data.totals.contributors === 1 ? 'account' : 'accounts'}`} />
            <StatTile size="md" tone="muted" value={count(data.totals.refused)} label="refused (429)" sub="rate limited, not stored" />
          </div>

          {/* The legend is always there: four states, never told apart by hue alone. */}
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 11.5, color: 'var(--color-neutral-400)' }}>
            {TIMELINE_SERIES.map((series) => (
              <span key={series} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Swatch colour={SERIES_META[series].colour} />
                {SERIES_META[series].label} · {count(data.totals[series])}
              </span>
            ))}
          </div>

          {/* The plots and the circuits side by side where there is room, stacked where not. */}
          <div style={{ display: 'flex', gap: '18px 32px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <div
            style={{ position: 'relative', flex: '1 1 520px', minWidth: 0, opacity: loading ? 0.6 : 1 }}
            onMouseLeave={() => setHover(null)}
          >
            {/* the uploads */}
            <div style={{ position: 'relative', height: PLOT_HEIGHT, marginLeft: GUTTER }}>
              {ticks.map((tick) => (
                <div
                  key={tick}
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    bottom: `${(tick / max) * 100}%`,
                    borderTop: `1px solid ${tick === 0 ? 'var(--color-neutral-700)' : 'var(--color-neutral-900)'}`,
                  }}
                >
                  <span style={{ position: 'absolute', right: '100%', marginRight: 8, top: -7, ...AXIS_TEXT }}>
                    {count(tick)}
                  </span>
                </div>
              ))}
              <div style={{ position: 'absolute', inset: 0, display: 'flex' }}>
                {buckets.map((bucket, index) => (
                  <div
                    key={bucket.at}
                    onMouseEnter={() => setHover(index)}
                    style={{
                      flex: 1,
                      minWidth: 0,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'flex-end',
                      background: hover === index ? 'color-mix(in srgb, var(--color-text) 5%, transparent)' : undefined,
                    }}
                  >
                    <Column bucket={bucket} max={max} active={hover === null || hover === index} />
                  </div>
                ))}
              </div>
            </div>

            {/* the time axis, shared */}
            <div style={{ display: 'flex', marginLeft: GUTTER, marginTop: 4, height: 14 }}>
              {buckets.map((bucket, index) => (
                <div key={bucket.at} style={{ flex: 1, minWidth: 0, position: 'relative' }}>
                  {labelled.has(index) ? (
                    <span style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', whiteSpace: 'nowrap', ...AXIS_TEXT }}>
                      {axisLabel(bucket.at, unit)}
                    </span>
                  ) : null}
                </div>
              ))}
            </div>

            {/* the refusals, on their own scale */}
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, margin: '12px 0 4px', marginLeft: GUTTER, fontSize: 11.5, color: 'var(--color-neutral-400)' }}>
              <span>Refused with 429</span>
              <span style={MUTED}>
                — not uploads, so not on the scale above
                {logStartsLate ? `; the request log begins ${shortDate(data.refused_from)}` : ''}
              </span>
            </div>
            <div style={{ position: 'relative', height: STRIP_HEIGHT, marginLeft: GUTTER, borderBottom: '1px solid var(--color-neutral-700)' }}>
              <span style={{ position: 'absolute', right: '100%', marginRight: 8, top: -5, ...AXIS_TEXT }}>
                {count(refusedMax)}
              </span>
              <div style={{ position: 'absolute', inset: 0, display: 'flex' }}>
                {buckets.map((bucket, index) => (
                  <div
                    key={bucket.at}
                    onMouseEnter={() => setHover(index)}
                    style={{
                      flex: 1,
                      minWidth: 0,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'flex-end',
                      background:
                        bucket.refused === null
                          ? // Before the log begins: unknown, which is not zero.
                            'repeating-linear-gradient(135deg, transparent 0 4px, var(--color-neutral-900) 4px 5px)'
                          : hover === index
                            ? 'color-mix(in srgb, var(--color-text) 5%, transparent)'
                            : undefined,
                    }}
                  >
                    {bucket.refused ? (
                      <div
                        style={{
                          width: 'min(22px, 72%)',
                          height: `${(bucket.refused / refusedMax) * 100}%`,
                          minHeight: 3,
                          background: 'var(--color-neutral-500)',
                          borderRadius: '4px 4px 0 0',
                        }}
                      />
                    ) : null}
                  </div>
                ))}
              </div>
            </div>

            {hovered && hover !== null ? (
              <div
                role="status"
                style={{
                  position: 'absolute',
                  top: 0,
                  // Pinned to the hovered slot, and flipped past the midpoint
                  // so it never leaves the card.
                  ...(hover < buckets.length / 2
                    ? { left: `calc(${GUTTER}px + (100% - ${GUTTER}px) * ${(hover + 1) / buckets.length} + 6px)` }
                    : { right: `calc((100% - ${GUTTER}px) * ${(buckets.length - hover) / buckets.length} + 6px)` }),
                  pointerEvents: 'none',
                  minWidth: 168,
                  padding: '8px 10px',
                  fontSize: 12,
                  background: 'var(--color-bg)',
                  border: '1px solid var(--color-divider)',
                  borderRadius: 'var(--radius-md)',
                  boxShadow: '0 6px 20px rgb(0 0 0 / 0.35)',
                  zIndex: 1,
                }}
              >
                <div style={{ fontWeight: 600, marginBottom: 6 }}>{bucketTitle(hovered.at, unit)}</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
                  <span>Submitted</span>
                  <span style={{ fontVariantNumeric: 'tabular-nums' }}>{count(bucketTotal(hovered))}</span>
                </div>
                {TIMELINE_SERIES.filter((series) => hovered[series] > 0).map((series) => (
                  <div key={series} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, color: 'var(--color-neutral-400)' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <Swatch colour={SERIES_META[series].colour} />
                      {SERIES_META[series].label}
                    </span>
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>{count(hovered[series])}</span>
                  </div>
                ))}
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, marginTop: 6, paddingTop: 6, borderTop: '1px solid var(--color-divider)', color: 'var(--color-neutral-400)' }}>
                  <span>Refused (429)</span>
                  <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {hovered.refused === null ? 'no record' : count(hovered.refused)}
                  </span>
                </div>
              </div>
            ) : null}
          </div>

          {data.top_circuits.length > 0 ? (
            <div style={{ flex: '0 1 340px', minWidth: 240 }}>
              <div style={{ fontSize: 11.5, color: 'var(--color-neutral-400)', marginBottom: 8 }}>
                Busiest circuits in this range
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 64px auto', gap: '6px 10px', alignItems: 'center', fontSize: 12.5 }}>
                {data.top_circuits.map((circuit) => (
                  <div key={circuit.official_id} style={{ display: 'contents' }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={`${circuit.track_name} · ${circuit.official_id}`}>
                      {circuit.track_name}
                    </span>
                    <span aria-hidden="true" style={{ height: 6, display: 'flex' }}>
                      <span
                        style={{
                          width: `${(circuit.uploads / busiest) * 100}%`,
                          minWidth: 3,
                          background: 'var(--color-neutral-600)',
                          borderRadius: '0 3px 3px 0',
                        }}
                      />
                    </span>
                    <span style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                      {count(circuit.uploads)}
                      <span style={MUTED}> · {count(circuit.pending)} queued</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p style={{ flex: '0 1 340px', margin: 0, fontSize: 12.5, ...MUTED }}>
              Nothing was submitted in this range.
            </p>
          )}
          </div>

          <details style={{ fontSize: 12 }}>
            <summary style={{ cursor: 'pointer', color: 'var(--color-neutral-400)' }}>Show as a table</summary>
            <table style={{ marginTop: 8, borderCollapse: 'collapse', fontVariantNumeric: 'tabular-nums' }}>
              <thead>
                <tr style={{ textAlign: 'right', color: 'var(--color-neutral-500)' }}>
                  <th style={{ textAlign: 'left', padding: '2px 14px 2px 0', fontWeight: 500 }}>{unit === 'hour' ? 'Hour (UTC)' : 'Day (UTC)'}</th>
                  <th style={{ padding: '2px 14px', fontWeight: 500 }}>Submitted</th>
                  {TIMELINE_SERIES.map((series) => (
                    <th key={series} style={{ padding: '2px 14px', fontWeight: 500 }}>{SERIES_META[series].label}</th>
                  ))}
                  <th style={{ padding: '2px 0 2px 14px', fontWeight: 500 }}>Refused</th>
                </tr>
              </thead>
              <tbody>
                {buckets
                  .filter((bucket) => bucketTotal(bucket) > 0 || (bucket.refused ?? 0) > 0)
                  .map((bucket) => (
                    <tr key={bucket.at} style={{ textAlign: 'right' }}>
                      <td style={{ textAlign: 'left', padding: '2px 14px 2px 0' }}>{bucketTitle(bucket.at, unit)}</td>
                      <td style={{ padding: '2px 14px' }}>{count(bucketTotal(bucket))}</td>
                      {TIMELINE_SERIES.map((series) => (
                        <td key={series} style={{ padding: '2px 14px' }}>{count(bucket[series])}</td>
                      ))}
                      <td style={{ padding: '2px 0 2px 14px' }}>{bucket.refused === null ? 'no record' : count(bucket.refused)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </details>
        </>
      ) : error ? null : (
        <div style={{ height: PLOT_HEIGHT, display: 'grid', placeItems: 'center', fontSize: 12.5, ...MUTED }}>
          counting uploads…
        </div>
      )}
    </Card>
  );
}

export default UploadTimelineCard;
